'use client'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ApiClientError, fetchApi } from '@/lib/api/client'
import { normalizeDimensions } from '@/hooks/api/use-report-breakdown'
import type { ReportDimension } from '@/lib/population/types'
import { isDimensionId } from './org'
import { vcroKeys } from './query-keys'
import type {
  DimensionId,
  VcroBreakdown,
  VcroConcentration,
  VcroHeatmap,
  VcroMovers,
  VcroOrgTrend,
  VcroOutcomeRow,
  VcroOverview,
  VcroWorkspaces,
} from './types'

/**
 * The wave 2 routes (contract: docs/product/modules/vcro/2026-10-03-wave-2-
 * where-the-risk-sits.md, W2-API), read through the app's API client, which
 * attaches the session and the workspace header. Every figure arrives from
 * the API; nothing here derives one.
 */

/** A reportable dimension, as /vcro/dimensions sends it (the wave 0 shape) with a vCRO id. */
export type VcroDimension = ReportDimension & { id: DimensionId }

/** Only the five kinds the contract names: a dimension the vCRO routes could not answer is not offered. */
export function normalizeVcroDimensions(raw: unknown): VcroDimension[] {
  return normalizeDimensions(raw).filter((d): d is VcroDimension => isDimensionId(d.id))
}

export function useVcroOverview() {
  return useQuery({
    queryKey: vcroKeys.org.overview(),
    queryFn: () => fetchApi<VcroOverview>('/vcro/overview'),
  })
}

/** `from` and `to` are workspace days; null holds the request until they are known. */
export function useVcroOrgTrend(range: { from: string; to: string } | null) {
  return useQuery({
    queryKey: vcroKeys.org.trend(range?.from ?? '', range?.to ?? ''),
    queryFn: () => fetchApi<VcroOrgTrend>(`/vcro/trend?${new URLSearchParams({ from: range!.from, to: range!.to })}`),
    enabled: range !== null,
    placeholderData: keepPreviousData,
  })
}

export function useVcroDimensions() {
  return useQuery({
    queryKey: vcroKeys.org.dimensions(),
    queryFn: () => fetchApi<unknown>('/vcro/dimensions'),
    select: normalizeVcroDimensions,
  })
}

/** `qs` is breakdownRequestQuery(): the dimension and the population filter. */
export function useVcroBreakdown(qs: string) {
  return useQuery({
    queryKey: vcroKeys.org.breakdown(qs),
    queryFn: () => fetchApi<VcroBreakdown>(`/vcro/breakdown?${qs}`),
    placeholderData: keepPreviousData,
  })
}

export function useVcroHeatmap(dimension: DimensionId) {
  return useQuery({
    queryKey: vcroKeys.org.heatmap(dimension),
    queryFn: () => fetchApi<VcroHeatmap>(`/vcro/heatmap?${new URLSearchParams({ dimension })}`),
    placeholderData: keepPreviousData,
  })
}

export function useVcroConcentration() {
  return useQuery({
    queryKey: vcroKeys.org.concentration(),
    queryFn: () => fetchApi<VcroConcentration>('/vcro/concentration'),
  })
}

export function useVcroMovers(dimension: DimensionId) {
  return useQuery({
    queryKey: vcroKeys.org.movers(dimension),
    queryFn: () => fetchApi<VcroMovers>(`/vcro/movers?${new URLSearchParams({ dimension })}`),
    placeholderData: keepPreviousData,
  })
}

/** A bare row array (W2-API); tolerates `{ rows }` too, so a wrapped answer never reads as "nothing sent". */
function outcomeRows(raw: unknown): VcroOutcomeRow[] {
  if (Array.isArray(raw)) return raw as VcroOutcomeRow[]
  const rows = raw && typeof raw === 'object' ? (raw as { rows?: unknown; data?: unknown }) : {}
  if (Array.isArray(rows.rows)) return rows.rows as VcroOutcomeRow[]
  if (Array.isArray(rows.data)) return rows.data as VcroOutcomeRow[]
  return []
}

/** The organisation's outcomes when `scope` is null; a bucket's (`'' ` = not set) otherwise. */
export function useVcroOutcomes(scope: { dimension: DimensionId; bucket: string } | null) {
  return useQuery({
    queryKey: vcroKeys.org.outcomes(scope?.dimension ?? null, scope?.bucket ?? null),
    queryFn: () =>
      fetchApi<unknown>(scope ? `/vcro/outcomes?${new URLSearchParams({ dimension: scope.dimension, bucket: scope.bucket })}` : '/vcro/outcomes'),
    select: outcomeRows,
    placeholderData: keepPreviousData,
  })
}

/** A 403 (`vcro_company_scope_required`): the viewer is not a company owner or admin. A state, not a failure. */
export function isCompanyScopeRequired(error: unknown): boolean {
  return error instanceof ApiClientError && (error.errorCode === 'vcro_company_scope_required' || error.statusCode === 403)
}

export function useVcroWorkspaces(enabled: boolean) {
  return useQuery({
    queryKey: vcroKeys.org.workspaces(),
    queryFn: () => fetchApi<VcroWorkspaces>('/vcro/workspaces'),
    enabled,
    retry: (failures, error) => !isCompanyScopeRequired(error) && failures < 2,
  })
}
